import React, {
  useState,
  useRef,
  useCallback,
  MutableRefObject,
  useLayoutEffect,
} from "react";
import * as List from "@repo/ui/list";
import { useIntersectionObserver } from "@repo/ui/useIntersectionObserver";
import * as Viewport from "@/components/viewport";
import { SpacingVariants } from "../../../../../ui/src/types/spacing";
import ButtonIndicators from "../../carousel-button-indicator/button-indicators";
import { isMobile } from "../../utils";
import styles from "./carousel.module.css";

type FeatureCarouselProps = {
  slides: JSX.Element[];
  slideRefs?: MutableRefObject<(HTMLLIElement | null)[]>;
  isFeatureCarousel?: boolean;
  slideGap?: SpacingVariants;
  videoPlayTrack?: { [key: string]: boolean };
  handleSetVideoPlayTrack?: (videoId: string) => void;
};

const Carousel = ({
  slides,
  slideRefs,
  videoPlayTrack,
  handleSetVideoPlayTrack,
  isFeatureCarousel = false,
  slideGap,
}: FeatureCarouselProps) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(true);

  const carouselRef = useRef<HTMLUListElement>(null);

  const mobileView = isMobile();

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (target && typeof target.id === "string") {
          const index = Number(target.id);

          const slideElement = slideRefs?.current[index];
          const videoElement = slideElement?.querySelector?.("video");
          const videoId = videoElement?.id;

          if (isIntersecting) {
            setCurrentSlideIndex(index);
          }
          if (
            videoId &&
            videoPlayTrack &&
            handleSetVideoPlayTrack &&
            mobileView &&
            isFeatureCarousel &&
            isIntersecting !== undefined
          ) {
            handleSetVideoPlayTrack(videoId);
          }
        }
      });
    },
    [mobileView],
  );

  useIntersectionObserver({
    root: carouselRef.current,
    rootMargin: "0px",
    threshold: 0.5,
    containerReference: carouselRef.current || undefined,
    observableItems: slideRefs?.current as Element[] | undefined,
    callback: observerCallback,
  });

  const handleSlideIndexChange = useCallback((value: number) => {
    setCurrentSlideIndex(value);
  }, []);

  useLayoutEffect(() => {
    if (!carouselRef.current) return;

    const slideElements = carouselRef.current.querySelectorAll("li");

    const totalWidth = Array.from(slideElements).reduce(
      (width, slide) => width + slide.getBoundingClientRect().width,
      0,
    );

    const containerWidth = carouselRef.current.getBoundingClientRect().width;

    setIsScrollable(totalWidth > containerWidth);
  }, []);

  return (
    <>
      <Viewport.Large>
        <List.Root
          style={{ gap: `var(--spacing-${slideGap})` }}
          ref={carouselRef}
          snap
          snapOffset="var(--container-gutter)"
          className={styles.carouselContainer}
        >
          {slides}
        </List.Root>
      </Viewport.Large>
      <Viewport.Small>
        <List.Root
          ref={carouselRef}
          snap
          snapOffset="var(--container-gutter)"
          className={styles.carouselContainer}
          style={{
            justifyContent:
              !isFeatureCarousel && !isScrollable ? "center" : undefined,
            gap: `var(--spacing-${slideGap})`,
          }}
        >
          {slides}
        </List.Root>
        {isFeatureCarousel && slideRefs && (
          <ButtonIndicators
            slideRefs={slideRefs}
            totalSlideCount={slides.length}
            currentSlideIndex={currentSlideIndex}
            handleSlideIndexChange={handleSlideIndexChange}
          />
        )}
      </Viewport.Small>
    </>
  );
};

export default Carousel;
